import { useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalHeader,
    ModalBody,
    Flex,
    ModalContent,
    ModalFooter,
    Textarea,
    useToast,
    Button,
} from '@chakra-ui/react';
import ZaraButton from '../custom-components/buttons/Button';
import { useApolloClient, useMutation } from '@apollo/client';
import { useError } from '../../error-provider/ErrorProvider';
import { ChangeKnowledgeDataText } from '../../graphql/mutations/ChangeKnowledgeDataText';
import { UploadText } from '../../graphql/mutations/UploadText';
import useServerApi from 'hooks/useServerApi';

const ReadFullText = ({
    isOpen,
    onClose,
    fullText,
    setFullText,
    knowledgeId,
    knowledgeData,
    isOpenAddText,
    agentId,
    onUpdate,
}) => {
    const { showError } = useError();
    const toast = useToast();

    const apolloClient = useApolloClient();
    const [{ get }] = useServerApi();

    const [uploadText, { loading: textLoading }] = useMutation(UploadText, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const [changeText, { loading }] = useMutation(ChangeKnowledgeDataText, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const onAddText = async () => {
        try {
            await uploadText({
                variables: {
                    agentId: agentId,
                    text: fullText,
                },
                onCompleted(data) {
                    if (data?.uploadText?.__typename === 'UploadedKnowledgeDataSuccessType') {
                        toast({
                            title: data?.uploadText?.message,
                            status: 'success',
                            isClosable: true,
                        });

                        apolloClient.cache.modify({
                            id: `KnowledgeDataType:${agentId}`,
                            fields: {
                                knowledgeData() {
                                    return [...knowledgeData, data?.uploadText?.knowledgeData];
                                },
                            },
                        });
                    } else {
                        toast({
                            title: data?.uploadText?.message,
                            status: 'error',
                            isClosable: true,
                        });
                    }
                },
            });
        } catch (error) {
            console.log(error);
        } finally {
            if (onUpdate) onUpdate();
            onClose();
        }
    };

    const onChangeText = async () => {
        try {
            await changeText({
                variables: {
                    knowledgeDataId: knowledgeId,
                    text: fullText,
                },
                onCompleted(data) {
                    if (data?.changeKnowledgeDataText?.__typename === 'UploadedKnowledgeDataSuccessType') {
                        toast({
                            title: data?.changeKnowledgeDataText?.message,
                            status: 'success',
                            isClosable: true,
                        });

                        apolloClient.cache.modify({
                            id: `KnowledgeDataType:${agentId}`,
                            fields: {
                                knowledgeData() {
                                    return knowledgeData.map((knowledge) =>
                                        knowledge.id === knowledgeId ? { ...knowledge, text: fullText } : knowledge
                                    );
                                },
                            },
                        });
                    } else {
                        toast({
                            title: data?.changeKnowledgeDataText?.message,
                            status: 'error',
                            isClosable: true,
                        });
                    }
                },
            });
        } catch (error) {
            console.log(error);
        } finally {
            if (onUpdate) onUpdate();
            onClose();
        }
    };

    useEffect(() => {
        if (isOpen && !isOpenAddText && knowledgeId && agentId) {
            const fetchFullText = async () => {
                try {
                    const [success, data] = await get(`/rest_endpoints/agents/knowledge/${agentId}/${knowledgeId}`);
                    if (success && data?.knowledgeItem) {
                        setFullText(data.knowledgeItem.text || '');
                    }
                } catch (error) {
                    console.error('Error fetching full text:', error);
                }
            };
            fetchFullText();
        }
    }, [isOpen, isOpenAddText, knowledgeId, agentId, get, setFullText]);

    useEffect(() => {
        if (!isOpen) {
            setFullText('');
        }
    }, [isOpen, setFullText]);

    return (
        <Modal isCentered isOpen={isOpen} onClose={onClose} motionPreset='slideInBottom' size='xl'>
            <ModalOverlay />
            <ModalContent overflow='auto'>
                <ModalHeader className='tracking-tight font-bold text-[#050536]' fontSize='2xl'>
                    {isOpenAddText ? 'Add Text to Knowledge Base' : 'Full Text'}
                </ModalHeader>
                <ModalBody maxH={'calc(100vh - 100px)'} overflow='auto'>
                    <Textarea
                        placeholder='Enter text to add to the knowledge base...'
                        value={fullText}
                        onChange={(e) => setFullText(e.target.value)}
                        rows={6}
                    />
                </ModalBody>
                <ModalFooter p='16px 24px'>
                    <Flex alignItems='center' justifyContent='space-between' w='100%'>
                        <Button
                            isDisabled={loading || textLoading}
                            onClick={onClose}
                            variant='outline'
                            w='auto'
                            fontSize='sm'
                        >
                            Cancel
                        </Button>
                        <ZaraButton
                            isDisabled={!fullText?.trim()?.length}
                            isLoading={loading || textLoading}
                            onClick={() => {
                                if (isOpenAddText) {
                                    onAddText();
                                } else {
                                    onChangeText();
                                }
                            }}
                            w='auto'
                            fontSize='sm'
                        >
                            {isOpenAddText ? 'Add Text' : 'Update'}
                        </ZaraButton>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ReadFullText;
